import { ref } from 'vue';
import { ElMessageBox } from 'element-plus';

const PLAYBACK_GUARD_MESSAGE =
  '检测到当前播放环境可能影响学习记录准确性。为保障您的学习效果，请使用课程页面自带的播放功能继续学习。即将返回课程详情页…';

/** CC 播放器官方最高倍速；增量校验按此上限，避免间隔内 2x 播放、上传瞬间切回 1x 误报 */
export const DEFAULT_MAX_PLAYBACK_RATE = 2;
/** 安全培训班课程隐藏倍速后，防插件上限为 1 倍 */
export const SAFETY_TRAINING_MAX_PLAYBACK_RATE = 1;
const POSITION_DELTA_TOLERANCE = 5;

export function getMaxPlaybackRate(isSafetyTrainingClassCourse?: boolean): number {
  return isSafetyTrainingClassCourse
    ? SAFETY_TRAINING_MAX_PLAYBACK_RATE
    : DEFAULT_MAX_PLAYBACK_RATE;
}

export interface UploadSnapshot {
  videoID: string;
  classID: string;
  specialID: string;
  maxPosition: number;
}

const isPlaybackGuardHandled = ref(false);
const lastUploadSnapshot = ref<UploadSnapshot>({
  videoID: '',
  classID: '',
  specialID: '',
  maxPosition: 0,
});

let pauseHandler: (() => void) | null = null;
let gotoCourseDetailsHandler: (() => void) | null = null;
let redirectTimer: ReturnType<typeof setTimeout> | null = null;

export function registerPlaybackGuardHandlers(handlers: {
  pause: () => void;
  gotoCourseDetailsPage: () => void;
}) {
  pauseHandler = handlers.pause;
  gotoCourseDetailsHandler = handlers.gotoCourseDetailsPage;
}

/** 暂停播放页已注册的视频（非播放页无注册时为空操作） */
export function pauseRegisteredPlayback() {
  pauseHandler?.();
}

export function handlePlaybackGuard() {
  if (isPlaybackGuardHandled.value) return;
  isPlaybackGuardHandled.value = true;

  pauseHandler?.();

  ElMessageBox.alert(PLAYBACK_GUARD_MESSAGE, '温馨提示', {
    confirmButtonText: '我知道了',
    showClose: false,
    closeOnClickModal: false,
    customClass: 'playback-guard-message-box',
  }).catch(() => {});

  if (redirectTimer) {
    clearTimeout(redirectTimer);
  }
  redirectTimer = setTimeout(() => {
    gotoCourseDetailsHandler?.();
  }, 3000);
}

export function isEffectivePlaybackRateOverLimit(
  getCurrentSpeed: () => number,
  videoEl: HTMLVideoElement | null | undefined,
  maxRate: number = DEFAULT_MAX_PLAYBACK_RATE,
): boolean {
  const playerRate = Number(getCurrentSpeed()) || 1;
  const elementRate = Number(videoEl?.playbackRate) || 1;
  // 播放器重建/异常读数时忽略非有限值，避免误伤
  if (!Number.isFinite(playerRate) || !Number.isFinite(elementRate)) return false;
  const effectiveRate = Math.max(playerRate, elementRate);
  return effectiveRate > maxRate;
}

export function checkMaxPositionAnomaly(params: {
  videoID: string;
  classID: string;
  specialID: string;
  maxPosition: number;
  spaceTime: number;
  isDrag?: boolean;
  maxRate?: number;
}): boolean {
  if (params.isDrag) return false;
  // 无有效观看间隔时不做跳跃判定（切视频重置后、刚上报完等）
  if (!params.spaceTime || params.spaceTime <= 0) return false;

  const snap = lastUploadSnapshot.value;
  const sameContext =
    snap.videoID === params.videoID &&
    snap.classID === params.classID &&
    snap.specialID === params.specialID;

  if (!sameContext || snap.maxPosition <= 0) return false;

  const delta = params.maxPosition - snap.maxPosition;
  // 进度未前进或回退，不视为异常
  if (delta <= 0) return false;

  const maxRate = params.maxRate ?? DEFAULT_MAX_PLAYBACK_RATE;
  // 不按上传瞬间 speed 计算：间隔内可能全程 2x、上传前才切回 1x
  // 额外放宽一点网络/定时抖动，避免正常观看误伤
  const allowed = maxRate * params.spaceTime + POSITION_DELTA_TOLERANCE;
  return delta > allowed;
}

export function updateUploadSnapshot(snapshot: UploadSnapshot) {
  lastUploadSnapshot.value = { ...snapshot };
}

export function resetUploadSnapshot() {
  lastUploadSnapshot.value = {
    videoID: '',
    classID: '',
    specialID: '',
    maxPosition: 0,
  };
}

export function resetPlaybackGuardState() {
  isPlaybackGuardHandled.value = false;
  if (redirectTimer) {
    clearTimeout(redirectTimer);
    redirectTimer = null;
  }
}
